import { useState } from 'react';
import { Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import { User } from '@auth0/auth0-react';
import { Ecom, Firm } from 'types/types';
import FigmaModal, { ModalType } from 'components/modal/FigmaModal';
import {
  useLazyGetUsersQuery,
} from 'api/api';

type TableContext = 'users' | 'ecom' | 'firms' | 'projects';

type TableMenuButtonProps = {
  tableContext: TableContext;
  rowData: User | Firm | Ecom | any;  // any should be a project type
  index: number;
  deleteMutation: ((id: any) => Promise<any>) | (() => void); // Remove empty function type when all delete mutations are implemented
  disableDelete?: boolean;
  disableEdit?: boolean
  onEdit: () => void;
  onDelete?: VoidFunction;
};

const TableMenuButton = ({
  tableContext,
  rowData,
  onDelete,
  index,
  deleteMutation,
  disableDelete = false,
  disableEdit = false,
  onEdit
}: TableMenuButtonProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const [activeModal, setActiveModal] = useState<ModalType>('');
  const [triggerGetUsers] = useLazyGetUsersQuery();

  const menuContainerStyle = {
    position: 'relative' as const,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: index % 2 === 0 ? 'white' : '#E5E8EC',
    cursor: 'pointer'
  };

  const dotsContainerStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '3px',
    padding: '8px',
  };

  const dotStyle = {
    width: '3px',
    height: '3px',
    background: '#666666',
    borderRadius: '9999px',
  };

  const dropdownStyle = {
    position: 'absolute' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    right: 'calc(100% - 12px)',
    top: 0,
    width: '145px',
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    zIndex: 10000,
  };

  const buttonBaseStyle = {
    width: '100%',
    color: 'black',
    backgroundColor: 'white',
    textTransform: 'none',
    justifyContent: 'left',
    ':hover': {
      backgroundColor: '#E5E8EC'
    }
  };

  const handleDelete = async () => {
    if (tableContext === 'users') {
      const users = await triggerGetUsers().unwrap();
      const firmAdmins = users!.filter(user =>
        user.site_role === 'Firm Admin' && user.firm === rowData.firm
      );
      if (firmAdmins.length > 1) {
        setActiveModal('deleteUser');
      } else {
        setActiveModal('finalFirmAdminWarning');
      }
    } else if (tableContext === 'ecom') {
      if (rowData?.is_active) {
        alert('Cannot delete active version!');
      } else {
        setActiveModal('deleteEcom');
      }
    } else if (tableContext === 'projects') {
      setActiveModal('deleteProject');
    } else if (tableContext === 'firms') {
      setActiveModal('deleteFirm');
    } else {
      try {
        await deleteMutation(rowData.id);
      } catch (e) {
        console.error('Delete error:', e);
      }
    }
  };

  const handleModalResponse = async (response: string) => {
    if (response === 'Confirm') {
      try {
        if (activeModal === 'deleteProject') {
          await deleteMutation(rowData.id);
        } else if (activeModal === 'deleteFirm') {
          await deleteMutation(rowData.id);
        } else if (activeModal === 'deleteUser') {
          await deleteMutation(rowData.auth0_id);
        } else if (activeModal === 'deleteEcom') {
          await deleteMutation(rowData.version);
        }
        onDelete?.();
      } catch (e) {
        console.error('Delete operation error:', e);
      }
    }
    setActiveModal('');
  };

  return (
    <>
      <FigmaModal
        open={!!activeModal}
        onClose={() => setActiveModal('')}
        values={rowData}
        type={activeModal}
        onResponse={handleModalResponse}
      />
      <div
        style={menuContainerStyle}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <div style={dotsContainerStyle}>
          <div style={dotStyle} />
          <div style={dotStyle} />
          <div style={dotStyle} />
        </div>

        {isHovering && (
          <div style={dropdownStyle}>
            <>
              <Button
                onClick={onEdit}
                disabled={disableEdit}
                variant='contained'
                startIcon={tableContext === 'ecom' ? <StarIcon /> : <EditIcon />}
                sx={{
                  ...buttonBaseStyle,
                  borderRadius: '10px 10px 0 0',
                }}
              >
                {tableContext === 'ecom' ? 'Set as Active' : 'Edit'}
              </Button>
              <Button
                onClick={handleDelete}
                disabled={disableDelete}
                variant='contained'
                startIcon={<DeleteIcon />}
                sx={{
                  ...buttonBaseStyle,
                  borderRadius: '0 0 10px 10px',
                }}
              >
                Delete
              </Button>
            </>
          </div>
        )}
      </div>
    </>
  );
};

export default TableMenuButton;